import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import api from '../services/api'
import { saveAuth, clearPendingCheckIns, getPendingCheckIns } from '../utils/userData'
import { API_ENDPOINTS, ROUTES, COHORT_DISPLAY_NAMES } from '../utils/constants'

const CohortAuthVerify = ({ programSlug = 'build3-cohort-7' }) => {
  const cohortDisplayName = COHORT_DISPLAY_NAMES[programSlug] || 'Build3 Energizer Sessions'

  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState('verifying') // 'verifying', 'success', 'error'
  const [error, setError] = useState('')
  const [countdown, setCountdown] = useState(5)

  useEffect(() => {
    const token = searchParams.get('token')

    if (!token) {
      setStatus('error')
      setError('No verification token provided')
      return
    }

    verifyToken(token)
  }, [searchParams])

  useEffect(() => {
    if (status === 'success' && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown((prev) => prev - 1)
      }, 1000)
      return () => clearTimeout(timer)
    }

    if (status === 'success' && countdown === 0) {
      navigate(ROUTES.COHORT_DASHBOARD(programSlug))
    }
  }, [status, countdown, navigate])

  const verifyToken = async (token) => {
    try {
      // Use new unified auth endpoint
      const response = await api.get(`${API_ENDPOINTS.AUTH.VERIFY}?token=${token}&programSlug=${programSlug}`)

      if (response.data.success) {
        const { token: jwtToken } = response.data.data

        // Store auth token
        saveAuth(jwtToken, null)

        // Submit any pending check-ins
        await submitPendingCheckIns(jwtToken)

        setStatus('success')
      }
    } catch (err) {
      console.error('Verification error:', err)
      setStatus('error')
      setError(err.response?.data?.message || 'Verification failed. Please try again.')
    }
  }

  const submitPendingCheckIns = async (jwtToken) => {
    const pendingCheckIns = getPendingCheckIns()
    
    if (pendingCheckIns.length === 0) return

    for (const checkIn of pendingCheckIns) {
      try {
        // Submit the check-in using new endpoint
        await api.post(
          API_ENDPOINTS.PROGRAMS.CHECK_INS(programSlug),
          {
            sessionNumber: checkIn.sessionNumber,
            checkInType: checkIn.checkInType,
            energyLevel: checkIn.energyLevel,
            stressLevel: checkIn.stressLevel,
            emotionalState: checkIn.emotionalState,
            physicalState: checkIn.physicalState,
          },
          {
            headers: { Authorization: `Bearer ${jwtToken}` },
          }
        )

        // If there's feedback data, submit that too
        if (checkIn.feedback) {
          await api.post(
            API_ENDPOINTS.PROGRAMS.FEEDBACK(programSlug),
            {
              sessionNumber: checkIn.sessionNumber,
              ...checkIn.feedback,
            },
            {
              headers: { Authorization: `Bearer ${jwtToken}` },
            }
          )
        }
      } catch (error) {
        console.error('Error submitting pending check-in:', error)
        // Continue with other check-ins even if one fails
      }
    }

    // Clear pending check-ins after submission
    clearPendingCheckIns()
  }

  const renderContent = () => {
    switch (status) {
      case 'verifying':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              className="w-20 h-20 border-4 border-primary-600 border-t-transparent rounded-full mx-auto mb-6"
            />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Verifying your email...
            </h2>
            <p className="text-gray-600">
              Please wait while we verify your magic link
            </p>
          </motion.div>
        )

      case 'success':
        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15 }}
              className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <svg
                className="w-12 h-12 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <motion.path
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </motion.div>
            <h2 className="text-3xl font-bold text-gray-900 mb-3">
              Welcome Back! 🎉
            </h2>
            <p className="text-gray-600 text-lg mb-6">
              Your email has been verified successfully.
            </p>
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <p className="text-gray-600">
                Redirecting to dashboard in{' '}
                <span className="font-bold text-primary-600">{countdown}</span>{' '}
                seconds...
              </p>
            </div>
            <Link
              to={ROUTES.COHORT_DASHBOARD(programSlug)}
              className="inline-block px-8 py-4 bg-gradient-to-r from-primary-600 to-purple-600 text-white font-semibold rounded-xl hover:opacity-90 transition-opacity"
            >
              Go to Dashboard Now
            </Link>
          </motion.div>
        )

      case 'error':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15 }}
              className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <svg
                className="w-12 h-12 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </motion.div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Verification Failed
            </h2>
            <p className="text-red-600 mb-6">{error}</p>
            <div className="space-y-4">
              <p className="text-gray-600">
                The link may have expired or already been used.
              </p>
              <Link
                to="/"
                className="inline-block px-8 py-4 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 transition-colors"
              >
                Go to Home
              </Link>
            </div>
          </motion.div>
        )

      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-purple-50 to-pink-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 flex justify-center"
        >
          <img
            src="/logo.png"
            alt="Wellness Loop Logo"
            className="h-16 w-auto"
          />
        </motion.div>

        {/* Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-3xl shadow-2xl p-8 md:p-12"
        >
          {/* Decorative gradient */}
          <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-primary-600 via-purple-600 to-pink-500 rounded-t-3xl" />

          {renderContent()}
        </motion.div>

        {/* Footer */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-center text-gray-500 mt-6"
        >
          {cohortDisplayName}
        </motion.p>
      </div>
    </div>
  )
}

export default CohortAuthVerify

